// Internal dependencies
import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';

// External dependencies
import { UntilDestroy } from '@ngneat/until-destroy';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

// Modules
import { LoadingModule } from 'src/app/modules/loading.module';

// Components

// Services
import { LoadingService } from 'src/app/services/loading.service';
import { UserService } from 'src/app/services/user.service';
import { GroupService } from 'src/app/services/group.service';

// Interfaces
import { User } from 'src/app/structures/user';
import { Group } from 'src/app/structures/group';

interface RelatedUsersProps {
    viewOnly: boolean;
    structureid: number;
    relatedUsers: Array<User>;
    relatedGroups: Array<Group>;
    tutor: boolean;
}

// Pipes

/**
 *  fenetre de dialogue pour la deconnexion.
 */
@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-related-users',
    templateUrl: './related-users.component.html',
    styleUrls: ['./related-users.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        LoadingModule,
        MatDialogModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        InfiniteScrollDirective
    ]
})
export class RelatedUsersComponent implements OnInit {
    search: string = '';

    users: Array<User>;
    groups: Array<Group>;

    currentPageUsers: number;
    currentPageGroups: number;

    LIMIT = 30;

    showDisabledUsers: boolean = false;

    constructor(
        private dialogRef: MatDialogRef<RelatedUsersComponent>,
        @Inject(MAT_DIALOG_DATA) public data: RelatedUsersProps,
        private loadingService: LoadingService,
        private userService: UserService,
        private groupService: GroupService
    ) {}

    ngOnInit() {
        if (!this.data.viewOnly) {
            this.refreshData();
        }
    }

    // #region Getters

    getUsers() {
        this.currentPageUsers++;
        const params: any = {
            structureid: this.data.structureid,
            role: 'learner|prospect',
            search: this.search,
            offset: this.currentPageUsers * this.LIMIT,
            limit: this.LIMIT
        };
        if (!this.showDisabledUsers) {
            params.enabled = true;
        }

        if (this.currentPageUsers === 0) {
            this.loadingService.startLoading('userDetails', 'getUsers');
        } else {
            this.loadingService.startLoading('userDetails', 'getUsersNextPage');
        }
        this.userService.getUsers(params).subscribe(
            (data: User[]) => {
                this.users = this.users.concat(
                    data.filter((user: User) => {
                        if (
                            this.data.relatedUsers.findIndex(
                                (relatedUser: User) => relatedUser.id === user.id
                            ) > -1
                        ) {
                            return false;
                        }
                        return true;
                    })
                );

                this.loadingService.stopLoading('userDetails', 'getUsers');
                this.loadingService.stopLoading('userDetails', 'getUsersNextPage');
            },
            (error: HttpErrorResponse) => {
                this.loadingService.stopLoading('userDetails', 'getUsers');
                this.loadingService.stopLoading('userDetails', 'getUsersNextPage');
            }
        );
    }

    getGroups() {
        this.currentPageGroups++;
        const params = {
            structureid: this.data.structureid,
            type: 'learner',
            search: this.search,
            offset: this.currentPageGroups * this.LIMIT,
            limit: this.LIMIT
        };

        if (this.currentPageGroups === 0) {
            this.loadingService.startLoading('userDetails', 'getGroups');
        } else {
            this.loadingService.startLoading('userDetails', 'getGroupsNextPage');
        }
        this.groupService.getGroups(params).subscribe(
            (data: Array<Group>) => {
                this.groups = this.groups.concat(
                    data.filter((group: Group) => {
                        if (
                            this.data.relatedGroups.findIndex(
                                (relatedGroup: Group) => relatedGroup.id === group.id
                            ) > -1
                        ) {
                            return false;
                        }
                        return true;
                    })
                );

                this.loadingService.stopLoading('userDetails', 'getGroups');
                this.loadingService.stopLoading('userDetails', 'getGroupsNextPage');
            },
            (error: HttpErrorResponse) => {
                this.loadingService.stopLoading('userDetails', 'getGroups');
                this.loadingService.stopLoading('userDetails', 'getGroupsNextPage');
            }
        );
    }

    getUserIcon(user) {
        return user.roles.learner ? 'icon-apprenant' : 'icon-apprenant-prospect';
    }

    getString(key: string) {
        if (key === 'title') {
            return this.data.tutor
                ? 'Apprenants liés au tuteur'
                : "Apprenants liés à l'intervenant";
        } else if (key === 'current-related-users') {
            return this.data.tutor
                ? 'Utilisateurs et groupes liés au tuteur'
                : "Utilisateurs et groupes liés à l'intervenant";
        } else if (key === 'possible-related-users') {
            return this.data.tutor
                ? 'Utilisateurs pouvant être liés au tuteur'
                : "Utilisateurs pouvant être liés à l'intervenant";
        } else if (key === 'possible-related-groups') {
            return this.data.tutor
                ? 'Groupes pouvant être liés au tuteur'
                : "Groupes pouvant être liés à l'intervenant";
        }
    }

    isLoading(view?: string) {
        if (view) {
            return this.loadingService.isLoading('userDetails', view);
        } else {
            return this.loadingService.isLoading('userDetails');
        }
    }

    // #endregion Getters

    // #region Handlers

    refreshData(clear?: boolean) {
        if (clear) {
            this.search = '';
        }
        this.currentPageUsers = -1;
        this.users = [];
        this.getUsers();

        this.currentPageGroups = -1;
        this.groups = [];
        this.getGroups();
    }

    selectUser(user: User) {
        this.data.relatedUsers.push(
            this.users.splice(
                this.users.findIndex((relatedUser: User) => relatedUser.id === user.id),
                1
            )[0]
        );
        if (this.users.length < 10) {
            this.getUsers();
        }
    }

    selectGroup(group: Group) {
        this.data.relatedGroups.push(
            this.groups.splice(
                this.groups.findIndex((relatedGroup: User) => relatedGroup.id === group.id),
                1
            )[0]
        );
        if (this.groups.length < 10) {
            this.getGroups();
        }
    }

    unselectUser(relatedUser: User) {
        this.users.unshift(
            this.data.relatedUsers.splice(
                this.data.relatedUsers.findIndex((user: User) => user.id === relatedUser.id),
                1
            )[0]
        );
    }

    unselectGroup(relatedGroup: Group) {
        this.groups.unshift(
            this.data.relatedGroups.splice(
                this.data.relatedGroups.findIndex((user: User) => user.id === relatedGroup.id),
                1
            )[0]
        );
    }

    handleCloseDialog() {
        this.dialogRef.close({ users: this.data.relatedUsers, groups: this.data.relatedGroups });
    }

    toggleDisabledUsers() {
        this.showDisabledUsers = !this.showDisabledUsers;
        this.refreshData();
    }

    // #endregion Handlers
}
